import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getUserRoleNames as getUserRoleNamesService,
  createUserRole as createUserRoleService,
  listUserRoles as listUserRolesService,
  archiveUserRole as archiveUserRoleService,
  updateUserRole as updateUserRoleService,
  getUserRole as getUserRoleService,
} from "../../services/UserRoleService";
import {
  CreateUserRolePayload,
  UpdateUserRole,
  UserRoleState,
} from "../../interfaces/user-role.interface";

export const getUserRoleNames = createAsyncThunk(
  "userRole/getUserRoleNames",
  async (_, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const userRoleNames = await getUserRoleNamesService();
      return userRoleNames.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createUserRole = createAsyncThunk(
  "userRole/createUserRole",
  async ({ data }: { data: CreateUserRolePayload }, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const message = await createUserRoleService(data);
      return message;
    } catch (error) {
      return thunkAPI.rejectWithValue("Failed to create user role");
    }
  }
);

export const listUserRoles = createAsyncThunk(
  "userRole/listUserRoles",
  async (_, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const userRolesList = await listUserRolesService();
      return userRolesList.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const archiveUserRole = createAsyncThunk(
  "userRole/archiveUserRole",
  async (
    { data }: { data: { roleName: string; deletedBy: string } },
    thunkAPI
  ) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const message = await archiveUserRoleService(data);
      return message;
    } catch (error) {
      return thunkAPI.rejectWithValue("Failed to archive user role");
    }
  }
);

export const updateUserRole = createAsyncThunk(
  "userRole/updateUserRole",
  async ({ data }: { data: UpdateUserRole }, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const message = await updateUserRoleService(data);
      return message;
    } catch (error) {
      return thunkAPI.rejectWithValue("Failed to update user role");
    }
  }
);

export const getUserRole = createAsyncThunk(
  "userRole/getUserRole",
  async ({ data }: { data: { roleName: string } }, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const userRole = await getUserRoleService(data);
      return userRole.data;
    } catch (error) {
      return thunkAPI.rejectWithValue("Failed to retrieve user role");
    }
  }
);

const userRoleSlice = createSlice({
  name: "userRole",
  initialState: {
    userRoleNames: [],
    userRoles: [],
    status: "idle",
    error: null,
  } as UserRoleState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserRoleNames.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUserRoleNames.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userRoleNames = action.payload;
        state.error = null;
      })
      .addCase(getUserRoleNames.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(createUserRole.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createUserRole.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(createUserRole.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(listUserRoles.pending, (state) => {
        state.status = "loading";
      })
      .addCase(listUserRoles.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userRoles = action.payload;
        state.error = null;
      })
      .addCase(listUserRoles.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(archiveUserRole.pending, (state) => {
        state.status = "loading";
      })
      .addCase(archiveUserRole.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(archiveUserRole.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(updateUserRole.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(getUserRole.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUserRole.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(getUserRole.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

const userRoleReducer = userRoleSlice.reducer;

export default userRoleReducer;
